import React from "react";
import { Button, Modal, Skeleton, toast } from "@picklebet/ui";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { constructCurrency } from "common/currency";
import { parseError } from "@/utilities/errorsParser";
import { walletService } from "@/features/wallet/services/wallet";
import { useDepositAmountValidationSchema } from "@/features/wallet/hooks/useDepositAmountValidationSchema";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { usePendingDepositClaim } from "@/hooks/firestore/user/usePromotionClaims";
import { ActiveGameSessionDepositInfo } from "@/features/wallet/components/ActiveGameSessionDepositInfo";
import { useDepositWizard } from "@/features/wallet/components/deposit/hooks/useDepositWizard";
import {
  DepositContent,
  DepositContentSkeleton,
} from "../../blocks/deposit-content/DepositContent";
import { useReportSubmitting } from "@/features/wallet/hooks/useReportSubmitting";
import { DepositAmountInput } from "../../blocks/deposit-amount-input/DepositAmountInput";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { DepositMethodField } from "../../blocks/deposit-method-field/DepositMethodField";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";
import { MethodRowSkeleton } from "../../method-row/MethodRow";

import * as styles from "./SkrillPrimaryView.module.scss";

export const SkrillPrimaryViewSkeleton = () => {
  return (
    <DepositContentSkeleton>
      <AmountInputSkeleton />

      <SectionDivider />

      <Skeleton
        size="xs"
        span="half"
        className={styles.sectionHeaderSkeleton}
      />

      <MethodRowSkeleton />

      <Skeleton className={styles.buttonSkeleton} />
    </DepositContentSkeleton>
  );
};

export const SkrillPrimaryView = () => {
  const { amount, setAmount, setSubmitting } = useDepositWizard();
  const currency = useFiatCurrency();
  const { pendingDepositClaim, isLoading: claimLoading } =
    usePendingDepositClaim();
  const [schema, schemaLoading] = useDepositAmountValidationSchema();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  const onSubmit = async (values: { amount: number }) => {
    const result = await walletService.skrillDeposit(
      values.amount,
      pendingDepositClaim?.code,
      currency,
    );

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Deposit Failed",
        description: parseError(result.error),
      });
      return;
    }

    window.location.replace(
      `https://pay.skrill.com/app/?sid=${result.data.sid}`,
    );
  };
  const isLoading = schemaLoading || claimLoading;

  if (isLoading) {
    return <SkrillPrimaryViewSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DepositContent>
        <ActiveGameSessionDepositInfo
          className={styles.activeGameSession}
          hasCTA={false}
        />

        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <DepositAmountInput
              value={value}
              onChange={(next) => {
                onChange(next);
                setAmount(next);
              }}
              errorMessage={errors.amount?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <SectionDivider />

        <SectionHeader>Payment Method</SectionHeader>

        <DepositMethodField />

        <Modal.Footer>
          <Button
            className={styles.depositButton}
            type="submit"
            disabled={amount === 0 || isSubmitting}
            loading={isSubmitting}
          >
            Deposit{" "}
            {constructCurrency(amount * 100, { currencyType: currency })}
          </Button>
        </Modal.Footer>
      </DepositContent>
    </form>
  );
};
